// ADV-MKT-ALERT-TIMESTAMP-AUDIO-SMS-001 — alert timestamp helpers.
// Renders an exact ET clock time plus a live-updating relative age, e.g.
//   "9:31:42 AM ET · 45s ago"  /  "9:31:42 AM ET · 2m ago"  /  "9:31:42 AM ET · 3h ago".
// Never returns only "just now".

const _etTimeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
});

/** Parse an alert timestamp (ISO string or epoch ms/seconds) to epoch ms, or null. */
export function toEpochMs(ts: unknown): number | null {
  if (ts === null || ts === undefined || ts === '') return null;
  if (typeof ts === 'number' && Number.isFinite(ts)) {
    return ts > 1e12 ? ts : ts > 1e9 ? ts * 1000 : ts; // ms vs seconds heuristic
  }
  const s = String(ts);
  const n = Number(s);
  if (Number.isFinite(n) && /^\d+$/.test(s.trim())) return n > 1e12 ? n : n * 1000;
  const parsed = Date.parse(s);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Exact wall-clock time in US Eastern, e.g. "9:31:42 AM ET". */
export function formatEtTime(ms: number): string {
  try {
    return `${_etTimeFmt.format(new Date(ms))} ET`;
  } catch {
    return '';
  }
}

/** Compact relative age: 45s ago / 2m ago / 3h ago / 2d ago. Never "just now". */
export function relativeAge(ms: number, nowMs: number): string {
  let s = Math.floor((nowMs - ms) / 1000);
  if (s < 0) s = 0;
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Combined label "9:31:42 AM ET · 45s ago" from an alert timestamp + current time. */
export function formatAlertTime(ts: unknown, nowMs: number): string {
  const ms = toEpochMs(ts);
  if (ms === null) return '';
  const exact = formatEtTime(ms);
  const age = relativeAge(ms, nowMs);
  return exact ? `${exact} · ${age}` : age;
}
